/* Reports. Every colour is a token (ADR-0007, enforced by stylelint).

   **Rank, per 33-ui-build-spec.** On every block there are three levels and a reader can tell
   them apart at a glance: the figure (full ink, tabular, largest), its label and unit (muted,
   small), the caveat (small print, capped measure, and amber only where a person has to act).
   The screen's honesty copy is all still here. What changed is how loudly it speaks. */

/* ------------------------------------------------------------------------ a disclosure line */

/* A line is a requirement on the left and the figure on the right, ruled off from the next. The
   figure column always holds something: a figure, or a word saying why not. An unsupported line
   keeps its row, because an omission reads as nothing being required. */
.line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 24px;
  align-items: start;
  padding: 12px 0;
  border-top: 1px solid var(--line);
}

.line:first-child {
  border-top: 0;
  padding-top: 4px;
}

.body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.label {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

/* The regulation's own words, set apart from our heading by a rule, not by a box: a reader must
   be able to see which is which without being told. */
.requirement {
  display: block;
  padding-left: 9px;
  border-left: 2px solid var(--line-2);
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
  max-width: 92ch;
}

.cite {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

.cite > a {
  color: var(--muted);
}

/* Numbers only. `white-space: nowrap` lives on the parts rather than here, because a flex or grid
   item sized from nowrap content takes its basis from the un-wrapped width - fine for a figure
   and catastrophic the day a sentence ended up in this column. */
.figure {
  min-width: 11ch;
  max-width: 220px;
  text-align: right;
  font-variant-numeric: lining-nums tabular-nums;
}

.value {
  display: block;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.unit {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

/* No figure, said in the figure column. A neutral outline: the absence is a fact about the line,
   and a missing input the company can supply is a warm sentence in the body, not this word. */
.noFigure {
  display: inline-block;
  padding: 1px 9px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 500;
  white-space: nowrap;
}

.noFigureUnsupported {
  border-style: dashed;
  color: var(--muted);
}

/* Why there is no figure. Deliberately not a zero and deliberately not empty, and in the body
   beside the requirement rather than in the column meant for numbers. Warm: this is usually a
   thing the company can fix (an input, a statement to write). */
.missing {
  display: block;
  font-size: var(--t-min);
  color: var(--warn);
  line-height: 1.5;
  max-width: 92ch;
}

/* A line EmitIQ cannot produce is a fact about the product, not something to fix, so it is not
   amber. It stays on the screen at the weight of any other row. */
.missingFact {
  color: var(--ink-2);
}

.lineUnsupported .label {
  color: var(--ink-2);
}

/* B-19: a line the guidance asks for and the paragraph does not. It must not read like the others,
   so it keeps a warm chip - the one place a requirement and a recommendation sit side by side. */
.guidance {
  display: inline-block;
  align-self: flex-start;
  padding: 1px 8px;
  border-radius: var(--radius-pill);
  background: var(--warn-w);
  border: 1px solid var(--warn-line);
  color: var(--warn);
  font-size: var(--t-min);
  font-weight: 500;
}

/* A limitation is small print attached to the line it limits: a rule beside it, no box. */
.limitation {
  display: block;
  padding-left: 9px;
  border-left: 2px solid var(--warn-line);
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
  max-width: 92ch;
}

.total {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 2px solid var(--ink-2);
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: lining-nums tabular-nums;
}

.total > span:last-child {
  font-size: 18px;
  letter-spacing: -0.01em;
}

/* The GWP bases the stated figures rest on (M-01, SECR 20F via U-20): a methodology, not a figure,
   so it is one muted line above the lines rather than a card. */
.gwpBasis {
  margin: 0 0 8px;
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.5;
}

.gwpBasisLabel {
  color: var(--muted);
  font-weight: 600;
}

.gwpBasisCount {
  color: var(--muted);
}

/* -------------------------------------------------------------- statements the company writes */

/* Where the company writes what only it can say - 20D(4), 20F, 20G.
 *
 * `display: block` on a `<span>` is not decoration: an inline element ignores `width`, so the
 * textarea inside fell back to its intrinsic column count and squeezed the whole line to about
 * forty pixels, every word on its own row. */
.editor {
  display: block;
  width: 100%;
  margin-top: 6px;
}

.editor > textarea,
.editor > input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 7px 9px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--ink);
  font-family: var(--font);
  font-size: var(--t-dense);
  line-height: 1.45;
  resize: vertical;
}

.editor > textarea:focus,
.editor > input:focus {
  outline: 2px solid var(--focus);
  outline-offset: -1px;
  border-color: var(--focus);
}

.ratio {
  display: flex;
  gap: 6px;
  align-items: center;
  width: 100%;
}

.ratio > input {
  min-width: 0;
}

.ratio > input:first-child {
  flex: 0 0 96px;
}

.editorRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
}

.save {
  padding: 4px 12px;
  border-radius: var(--radius-ctl);
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--on-fill);
  font-size: var(--t-min);
  font-weight: 600;
  cursor: pointer;
}

.save:hover:not(:disabled) {
  background: var(--accent-h);
}

.save:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: not-allowed;
}

.saveNote {
  font-size: var(--t-min);
  color: var(--muted);
}

/* A refusal from the server, rendered where the person is looking rather than swallowed. */
.saveError {
  font-size: var(--t-min);
  color: var(--crit);
}

/* What the company has already said, shown above the box it is rewritten in. Distinct from the
   requirement above it, which is the regulation's words rather than the company's. */
.saidSoFar {
  display: block;
  margin-top: 4px;
  padding: 7px 10px;
  border-radius: var(--radius-ctl);
  background: var(--panel-2);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: var(--t-dense);
  line-height: 1.5;
  white-space: pre-wrap;
  max-width: 92ch;
}

/* ---------------------------------------------------------------- the verdict and its findings */

/* The verdict, at the top of its panel, where it cannot be missed. The edge carries the state and
   the first line names it in words; colour is the second signal, never the only one. "Not ready"
   is red because something blocks; "checks passed" is `--ok` and not the brand green, because the
   brand says "this is EmitIQ" and `--ok` says "a check passed". */
.verdict {
  display: grid;
  gap: 3px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-left-width: 3px;
  border-radius: var(--radius-ctl);
  margin-bottom: 12px;
}

.verdictNo {
  background: var(--crit-w);
  border-color: var(--crit-line);
  border-left-color: var(--crit);
}

.verdictYes {
  background: var(--ok-w);
  border-color: var(--ok-line);
  border-left-color: var(--ok);
}

.verdictHead {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: var(--t-body);
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
}

/* Beside the headline, the same ink: the word names the state, the glyph only helps the eye find
   it. `flex-shrink: 0` so a wrapping headline never squeezes it. */
.verdictGlyph {
  flex-shrink: 0;
  margin-top: 1px;
}

.verdictNo .verdictGlyph {
  color: var(--crit);
}

.verdictYes .verdictGlyph {
  color: var(--ok);
}

/* The counts: tabular, muted, directly under the headline. */
.verdictSub {
  display: block;
  font-size: var(--t-min);
  font-weight: 500;
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink-2);
}

/* Why the verdict is what it is, including what was not checked. A verdict with no reasoning is a
   grade, and a grade is what people argue with rather than act on. Small print, capped measure. */
.verdictWhy {
  display: block;
  margin-top: 4px;
  font-size: var(--t-min);
  font-weight: 400;
  line-height: 1.5;
  color: var(--ink-2);
  max-width: 72ch;
}

/* What is stopping the filing. Blocking and advisory must not look alike: one has to be fixed and
   the other has to be read. The severity is a word, so the difference is not a colour. */
.findings {
  margin-bottom: 12px;
}

.finding {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 9px 0;
  border-top: 1px solid var(--line);
}

.finding:first-child {
  border-top: 0;
  padding-top: 0;
}

.sev {
  justify-self: start;
  padding: 1px 7px;
  border: 1px solid var(--crit-line);
  border-radius: var(--radius-pill);
  background: var(--crit-w);
  color: var(--crit);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 1.4;
}

.sevAdvisory {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.findingBody {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.findingMessage {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.remedy {
  display: block;
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
}

.detail {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.5;
}

/* ---------------------------------------------------------------------------------- notices */

/* A caveat on a figure, set as an annotation: small, a rule beside it, capped measure. The title
   leads the sentence in weight rather than sitting on a line of its own. Neutral by default - an
   exclusion that is correctly disclosed is not a warning, and colouring every one amber makes
   the screen look alarmed about its own honesty. */
.notice {
  margin: 0 0 10px;
  padding: 1px 0 1px 10px;
  border-left: 2px solid var(--line-2);
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.55;
  max-width: 92ch;
}

/* Amber only where a person has to act or where the figure is not yet law. */
.noticeWarn {
  border-left-color: var(--warn);
}

.noticeWarn .noticeTitle {
  color: var(--warn);
}

.noticeTitle {
  display: block;
  font-weight: 600;
  color: var(--ink);
}

/* One named fact inside a notice - a company and when it joined the group, a site and when it
   closed. Its own line, because two of them run together as one sentence. */
.noticeLine {
  display: block;
  margin-bottom: 2px;
}

/* The fine print: what a filed report is, where the receipts are, whose verification this is.
   Rank 3, kept whole. */
.finePrint {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
}

.fine {
  margin: 0 0 8px;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.55;
  max-width: 92ch;
}

.fine strong {
  color: var(--ink-2);
}

/* ------------------------------------------------------------------------------- the filings */

/* Filings (ADR-0020). A filing is a figure, a date and a digest, and the digest is the row's most
   important line rather than a column squeezed to fit. */
.filings {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
}

.filing {
  display: grid;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid var(--line);
}

.filing:first-child {
  border-top: 0;
  padding-top: 0;
}

.filingMain {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.filingTitle {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
  font-size: var(--t-dense);
  font-weight: 500;
  color: var(--muted);
}

.filingFigure {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.filingWhen {
  margin-left: 6px;
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
}

.filingVersion {
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.4;
}

.filingVerdict {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

/* The digest, in full and selectable. A truncated hash is decoration; a reader checking one needs
   all sixty-four characters, and shortening it would make the page look tidier and the claim
   uncheckable. Set in a quiet well so it reads as a value to copy, not as prose. */
.digest {
  margin-top: 2px;
  padding: 5px 8px;
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  font-family: var(--mono);
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--ink-2);
  overflow-wrap: anywhere;
  user-select: all;
}

.filingActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
}

.filingActions .fine {
  margin: 0;
}

/* Re-derivation (ADR-0020). Two questions, two rows: the record's own integrity (a failure is
   serious) and whether today's data still reproduces it (false is an ordinary restatement). */
.rederivation {
  display: grid;
  gap: 6px;
  padding: 9px 11px;
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  background: var(--panel-2);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.checks {
  display: grid;
  gap: 3px;
}

.checkRow {
  display: grid;
  grid-template-columns: 14ch minmax(0, 1fr);
  gap: 10px;
  align-items: baseline;
}

.checkQuestion {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
}

.checkAnswer {
  font-weight: 600;
  color: var(--ink);
}

.checkOk {
  color: var(--ok);
}

.checkBad {
  color: var(--crit);
}

/* Not red, not amber: the world moved, which is the ordinary thing a filing exists to be compared
   against. */
.checkMoved {
  color: var(--info);
}

.checkWhy {
  display: block;
  max-width: 72ch;
}

.pack {
  margin: 0 0 10px;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.55;
  max-width: 92ch;
}

.issueRow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0 8px;
}

/* Closing the period (CLS-01). **The blockers are the panel**: each one is a different place to
   go - a census gap is a document to ask somebody for, one in review is a document to look at, an
   open approval is a person to chase. A single readiness bar would send all three to the same
   place, which is the shape of progress indicator nobody acts on. */
.blockers {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: grid;
  gap: 0;
}

.blocker {
  display: grid;
  gap: 2px;
  padding: 8px 0 8px 10px;
  border-left: 2px solid var(--warn);
  border-top: 1px solid var(--line);
}

.blocker:first-child {
  border-top: 0;
}

.blockerWhat {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.blockerRemedy {
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.55;
  max-width: 92ch;
}

.statline {
  margin: 0 0 10px;
  font-size: var(--t-dense);
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink-2);
}

/* The verification pack (ASR-03). A link, not a button: it is a file the browser saves. */
.packLink {
  color: var(--accent);
  font-weight: 600;
  text-decoration: none;
}

.packLink:hover {
  text-decoration: underline;
}

/* ---------------------------------------------------------------------- against last year */

/* Site-by-site movement against the prior year (ACC-10). Rows, not boxes: each is a site, a figure
   and a sentence, and the sentence is the content - a table would make the explanation a narrow
   last column nobody reads. */
.movements {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}

.movement {
  display: grid;
  gap: 2px;
  padding: 9px 0;
  border-top: 1px solid var(--line);
}

.movement:first-child {
  border-top: 0;
}

.movementSite {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.movementFigures {
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink-2);
}

.movementWhy {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
  max-width: 92ch;
}

/* An unexplained movement is a finding, not an absence. `--warn`, not `--crit`: the figure may be
   perfectly correct and a real reduction, and red would say somebody had done something wrong. */
.movementUnexplained {
  color: var(--warn);
}

/* Which records a figure apportions, and the fraction that cut each one (rule 3, EXP-36). Shown
   only where there is one, so it never competes with the figure itself for attention. */
.apportioned {
  display: block;
  font-size: var(--t-min);
}

.apportionedToggle {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.apportionedList {
  margin: 4px 0 0;
  padding-left: 14px;
  color: var(--ink-2);
  font-variant-numeric: lining-nums tabular-nums;
}

.apportionedList li {
  margin-bottom: 2px;
}

/* The year-on-year panel's totals (ACC-10, UX-01): one ruled strip rather than five boxes, so the
   bridge reads left to right as prior, boundary, unexplained, residual, current. A cell is a
   figure over its label. */
.counts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin: 12px 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  overflow: hidden;
}

.count {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 9px 12px;
  border-left: 1px solid var(--line);
}

.count:first-child {
  border-left: 0;
}

.countN {
  font-size: var(--t-head);
  font-weight: 600;
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink);
}

.countLabel {
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.4;
}

/* The control that lists the sites behind the residual. A 24px target (tokens.css `--target-min`),
   left-aligned under the label it belongs to. */
.residualToggle {
  align-self: flex-start;
  min-height: var(--target-min);
  font-size: var(--t-min);
  text-align: left;
}

/* The two windows the panel is comparing. First, because every figure under it is a difference
   between them and a difference with no stated endpoints cannot be checked. */
.bridgeWindows {
  margin: 0;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink-2);
}

/* What the panel accounts for and what it does not. Muted and below the figures: it qualifies
   them rather than competing with them, and design rule 5 is that the qualification is said. */
.bridgeScope {
  margin: 10px 0 0;
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--muted);
  max-width: 92ch;
}

/* The first year's next action. The sentence above it describes a capability; this is the way to
   it, which is EXP-29's "next action" rather than a flourish. */
.bridgeNext {
  margin: 8px 0 0;
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 92ch;
}

/* ------------------------------------------------------------------------------ narrow */

@media (max-width: 700px) {
  .line {
    grid-template-columns: minmax(0, 1fr);
  }

  .figure {
    text-align: left;
    max-width: none;
  }

  .checkRow {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .count {
    border-left: 0;
    border-top: 1px solid var(--line);
  }

  .count:first-child {
    border-top: 0;
  }
}

/* ------------------------------------------------------------------ a filing, opened (tabs) */

/* The verdict and the facts beside it, over the tabs. */
.head {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
}

.head .verdict {
  margin-bottom: 0;
}

.facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

.wide {
  grid-column: 1 / -1;
  margin: 0;
}

.tabsRow {
  grid-column: 1 / -1;
  min-width: 0;
}

.tabBody {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

/* "Checks passed" is information, not approval: the info ramp rather than the pass colour, so the
   top of the machine ladder never reads as a green light (F07). */
.verdictInfo {
  background: var(--info-w);
  border-color: var(--info-line);
  border-left-color: var(--info);
}

.verdictInfo .verdictGlyph {
  color: var(--info);
}

/* The long explanations, collapsed and never deleted. */
.basisBox {
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.5;
}

.basisBox > summary {
  cursor: pointer;
  color: var(--ink-2);
  font-weight: 600;
}

@media (width <= 900px) {
  .facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 600px) {
  .facts {
    grid-template-columns: minmax(0, 1fr);
  }
}
